import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "sonner";
import LandingPage from "@/pages/LandingPage";
import ContactPage from "@/pages/ContactPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import NotificationWizard from "@/pages/NotificationWizard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import DashboardPage from "@/pages/DashboardPage";
import CertificateSelectPage from "@/pages/CertificateSelectPage";
import DomesticEICWizard from "@/pages/DomesticEICWizard";
import MinorWorksWizard from "@/pages/MinorWorksWizard";
import ThreeCircuitMWWizard from "@/pages/ThreeCircuitMWWizard";
import MCSFormWizard from "@/pages/MCSFormWizard";
import ContractorProfilePage from "@/pages/ContractorProfilePage";
import GenericElecCertWizard from "@/pages/GenericElecCertWizard";
import CreditSuccessPage from "@/pages/CreditSuccessPage";
import AdminPage from "@/pages/AdminPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import PATTestingWizard from "@/pages/PATTestingWizard";
import GasSafeWizard from "@/pages/GasSafeWizard";
import GasSafeSelectPage from "@/pages/GasSafeSelectPage";
import GasSafeGenericWizard from "@/pages/GasSafeGenericWizard";
import DeleteAccountPage from "@/pages/DeleteAccountPage";
import CustomerPortalPage from "@/pages/CustomerPortalPage";
import SmsPage from "@/pages/SmsPage";
import BatchUploadPage from "@/pages/BatchUploadPage";
import { fetchAndCacheCatalog } from "@/utils/schemaCache";
import { startSyncListener, processSyncQueue } from "@/utils/syncQueue";
import OfflineBanner from "@/components/OfflineBanner";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="loading-spinner">
        <div className="border-2 border-[#0A0A0A] p-8 bg-white" style={{ boxShadow: '4px 4px 0px #0A0A0A' }}>
          <div className="w-8 h-8 border-4 border-[#D4A537] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm font-medium text-[#71717A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function App() {
  // Prefetch catalog and start offline sync listener
  useEffect(() => {
    fetchAndCacheCatalog().catch(() => {});
    startSyncListener();
    processSyncQueue().catch(() => {});
  }, []);

  return (
    <ThemeProvider>
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <OfflineBanner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/view/:token" element={<CustomerPortalPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/notification" element={<ProtectedRoute><NotificationWizard /></ProtectedRoute>} />
          <Route path="/certificates" element={<ProtectedRoute><CertificateSelectPage /></ProtectedRoute>} />
          <Route path="/new/electrical" element={<ProtectedRoute><CertificateSelectPage /></ProtectedRoute>} />
          <Route path="/new/mcs" element={<ProtectedRoute><CertificateSelectPage /></ProtectedRoute>} />
          <Route path="/certificates/domestic-eic" element={<ProtectedRoute><DomesticEICWizard /></ProtectedRoute>} />
          <Route path="/certificates/minor-works" element={<ProtectedRoute><MinorWorksWizard /></ProtectedRoute>} />
          <Route path="/certificates/3-circuit-minor" element={<ProtectedRoute><ThreeCircuitMWWizard /></ProtectedRoute>} />
          <Route path="/certificates/mcs/:formId" element={<ProtectedRoute><MCSFormWizard /></ProtectedRoute>} />
          <Route path="/certificates/elec/:formId" element={<ProtectedRoute><GenericElecCertWizard /></ProtectedRoute>} />
          <Route path="/certificates/pat-testing" element={<ProtectedRoute><PATTestingWizard /></ProtectedRoute>} />
          <Route path="/new/gas-safe" element={<ProtectedRoute><GasSafeSelectPage /></ProtectedRoute>} />
          <Route path="/certificates/gas-safe/boiler-commissioning" element={<ProtectedRoute><GasSafeWizard /></ProtectedRoute>} />
          <Route path="/certificates/gas-safe/:formId" element={<ProtectedRoute><GasSafeGenericWizard /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ContractorProfilePage /></ProtectedRoute>} />
          <Route path="/credits/success" element={<ProtectedRoute><CreditSuccessPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/sms" element={<ProtectedRoute><SmsPage /></ProtectedRoute>} />
          <Route path="/batch-upload" element={<ProtectedRoute><BatchUploadPage /></ProtectedRoute>} />
          <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
